import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

interface AppointmentLinkResponse {
  link: string;
}

/**
 * Resolves the personalised "Umów konsultację" booking link.
 *
 * The appointment/consultation data lives only in the LEGACY Ekowitryna
 * platform (Laravel API at `platformaLegacyApiUrl`), which is not covered by
 * the generated Angular API clients. The legacy endpoint returns a booking URL
 * built from the signed-in user's active appointment services.
 *
 * The Bearer token is attached manually here because the app's HTTP
 * interceptors target `environment.apiUrl`, while the legacy API is a
 * different host and would otherwise receive no token.
 */
@Injectable({ providedIn: 'root' })
export class AppointmentService {
  /** Static booking page used when the legacy API is unreachable or returns no link. */
  static readonly FALLBACK_URL = 'https://ekowitryna.pl/konsultacje/';

  private readonly http = inject(HttpClient);
  private readonly keycloakService = inject(Keycloak);

  private readonly linkUrl = `${environment.platformaLegacyApiUrl}/appointments/link`;

  /**
   * Fetches the user-specific consultation booking link from the legacy API.
   * Emits {@link AppointmentService.FALLBACK_URL} when the response has no link.
   */
  getConsultationLink(): Observable<string> {
    return this.http
      .get<AppointmentLinkResponse>(this.linkUrl, { headers: this.authHeaders() })
      .pipe(map((response) => response.link || AppointmentService.FALLBACK_URL));
  }

  private authHeaders(): Record<string, string> {
    const token = this.keycloakService.token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  }
}
